import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Plus, Calendar, Mic, Square, Loader2, Eye } from "lucide-react";
import Layout from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../../App";
import { fmtDateTime } from "./_rentalHelpers";

const VISIT_STATUS_LABELS = {
  scheduled: "Scheduled", completed: "Completed", no_show: "No show",
  cancelled: "Cancelled", rescheduled: "Rescheduled",
};
const VISIT_STATUS_COLORS = {
  scheduled: "bg-violet-100 text-violet-700",
  completed: "bg-emerald-100 text-emerald-700",
  no_show: "bg-rose-100 text-rose-700",
  cancelled: "bg-slate-100 text-slate-600",
  rescheduled: "bg-amber-100 text-amber-700",
};

const emptyVisit = {
  tenant_lead_id: "", rental_unit_id: "",
  scheduled_at: "", status: "scheduled",
  feedback: "", notes: "",
};

const VoiceRecorder = ({ visitId, onTranscribed }) => {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Recording too short"); return; }
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "visit.webm");
          const res = await axios.post(`${API}/rentals/visits/${visitId}/voice-feedback`, fd);
          toast.success("Voice transcribed");
          onTranscribed?.(res.data);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Transcription failed");
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Mic access denied");
    }
  };
  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  if (uploading) return <Button variant="outline" size="sm" disabled><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Transcribing…</Button>;
  return recording
    ? <Button variant="destructive" size="sm" onClick={stop} data-testid="stop-voice"><Square className="w-3.5 h-3.5 mr-1" /> Stop</Button>
    : <Button variant="outline" size="sm" onClick={start} data-testid="record-voice"><Mic className="w-3.5 h-3.5 mr-1" /> Record</Button>;
};

export default function RentalVisitsPage() {
  const [visits, setVisits] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVisit);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const [v, u, t] = await Promise.all([
        axios.get(`${API}/rentals/visits`, { params }),
        units.length ? Promise.resolve({ data: units }) : axios.get(`${API}/rentals/units`, { params: { limit: 500 } }),
        tenants.length ? Promise.resolve({ data: tenants }) : axios.get(`${API}/rentals/tenant-leads`, { params: { limit: 500 } }),
      ]);
      setVisits(v.data || []);
      setUnits(u.data || []);
      setTenants(t.data || []);
    } catch (e) {
      toast.error("Failed to load visits");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...emptyVisit, scheduled_at: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16) });
    setShowCreate(true);
  };
  const startEdit = (v) => {
    setEditing(v);
    setForm({
      ...emptyVisit, ...v,
      scheduled_at: v.scheduled_at ? new Date(v.scheduled_at).toISOString().slice(0, 16) : "",
    });
    setShowCreate(true);
  };

  const submit = async () => {
    if (!form.tenant_lead_id || !form.rental_unit_id || !form.scheduled_at) {
      toast.error("Tenant, unit and scheduled time required"); return;
    }
    const payload = {
      ...form,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
    };
    try {
      if (editing) {
        await axios.put(`${API}/rentals/visits/${editing.rental_visit_id}`, {
          scheduled_at: payload.scheduled_at, status: payload.status,
          feedback: payload.feedback, notes: payload.notes,
        });
        toast.success("Visit updated");
      } else {
        await axios.post(`${API}/rentals/visits`, payload);
        toast.success("Visit scheduled");
      }
      setShowCreate(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const setStatus = async (v, status) => {
    try {
      await axios.put(`${API}/rentals/visits/${v.rental_visit_id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="rental-visits-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-emerald-600" /> Rental Visits
            </h1>
            <p className="text-sm text-slate-500">Schedule, track feedback & voice-log impressions</p>
          </div>
          <Button onClick={startCreate} data-testid="create-visit-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> New Visit
          </Button>
        </div>

        <Card className="p-3 mb-3 flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="visit-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(VISIT_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Scheduled</th>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Feedback</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Loading…</td></tr>}
                {!loading && visits.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No visits.</td></tr>}
                {visits.map(v => (
                  <tr key={v.rental_visit_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`visit-row-${v.rental_visit_id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(v.scheduled_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      <p className="font-medium">{v.tenant?.name || "—"}</p>
                      <p className="text-slate-500">{v.tenant?.phone}</p>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <p className="font-medium">{v.unit?.unit_number || "—"}</p>
                      <p className="text-slate-500">{v.unit?.project_name}{v.unit?.tower ? ` · ${v.unit.tower}` : ""}</p>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={VISIT_STATUS_COLORS[v.status] || "bg-slate-100"}>{VISIT_STATUS_LABELS[v.status] || v.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-xs truncate" title={v.feedback}>{v.feedback || "—"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <VoiceRecorder visitId={v.rental_visit_id} onTranscribed={() => load()} />
                      <Button variant="ghost" size="sm" onClick={() => startEdit(v)} data-testid={`edit-visit-${v.rental_visit_id}`}>Edit</Button>
                      {v.status === "scheduled" && (
                        <Button variant="ghost" size="sm" onClick={() => setStatus(v, "completed")} data-testid={`complete-visit-${v.rental_visit_id}`} className="text-emerald-700">Mark done</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Visit" : "Schedule Visit"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {!editing && (
                <>
                  <div>
                    <Label>Tenant *</Label>
                    <Select value={form.tenant_lead_id} onValueChange={v => setForm({ ...form, tenant_lead_id: v })}>
                      <SelectTrigger data-testid="visit-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                      <SelectContent>
                        {tenants.map(t => <SelectItem key={t.tenant_lead_id} value={t.tenant_lead_id}>{t.name} · {t.phone}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Unit *</Label>
                    <Select value={form.rental_unit_id} onValueChange={v => setForm({ ...form, rental_unit_id: v })}>
                      <SelectTrigger data-testid="visit-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                      <SelectContent>
                        {units.map(u => <SelectItem key={u.rental_unit_id} value={u.rental_unit_id}>{u.unit_number} · {u.project_name || ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label>When *</Label>
                <Input data-testid="visit-time-input" type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(VISIT_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Feedback</Label><Textarea rows={3} value={form.feedback} onChange={e => setForm({ ...form, feedback: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-visit" className="bg-emerald-600 hover:bg-emerald-700">{editing ? "Save" : "Schedule"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
